/* eslint-disable react/prop-types */
// AVI Forms — Admin tab.
// Lists every form, lets the admin create/edit/duplicate/disable a
// form and inspect the submissions received from candidates.
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { API } from "../App";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  ClipboardList,
  Plus,
  Copy,
  Edit2,
  Trash2,
  ExternalLink,
  Eye,
  ToggleLeft,
  ToggleRight,
  Loader2,
  Inbox,
  Download,
  X,
  GripVertical,
} from "lucide-react";

const FIELD_TYPES = [
  { value: "text", label: "Texto curto" },
  { value: "textarea", label: "Texto longo" },
  { value: "email", label: "E-mail" },
  { value: "phone", label: "Telefone (BR)" },
  { value: "cpf", label: "CPF" },
  { value: "rg", label: "RG" },
  { value: "pis", label: "PIS" },
  { value: "cep", label: "CEP" },
  { value: "date", label: "Data" },
  { value: "select", label: "Dropdown" },
  { value: "radio", label: "Múltipla escolha" },
  { value: "checkbox", label: "Caixas (várias)" },
  { value: "yesno", label: "Sim / Não" },
  { value: "file", label: "Upload de arquivo" },
];

const headers = (token) => ({ Authorization: `Bearer ${token}` });

const newId = () => Math.random().toString(36).slice(2, 14);

const AdminFormsTab = ({ token }) => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // form draft (create or edit)
  const [submissionsFor, setSubmissionsFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/forms`, {
        headers: headers(token),
      });
      setForms(r.data?.items || []);
    } catch {
      toast.error("Falha ao carregar formulários");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const startCreate = () =>
    setEditing({
      id: null,
      title: "",
      description: "",
      active: true,
      notification_email: "",
      fields: [],
    });

  const startEdit = (form) => setEditing({ ...form });

  const copyPublicLink = async (formId) => {
    const url = `${window.location.origin}/form/${formId}`;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
        toast.success("Link copiado!");
        return;
      }
      throw new Error("Clipboard API unavailable");
    } catch {
      // Fallback: hidden textarea + execCommand (works in any context).
      try {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (ok) {
          toast.success("Link copiado!");
          return;
        }
      } catch {
        /* ignore */
      }
      // Last resort: show the URL so the admin can copy manually.
      window.prompt("Copie o link manualmente:", url);
    }
  };

  const toggleActive = async (form) => {
    try {
      await axios.put(
        `${API}/admin/forms/${form.id}`,
        { active: !form.active },
        { headers: headers(token) },
      );
      toast.success(form.active ? "Formulário desativado" : "Formulário ativado");
      load();
    } catch {
      toast.error("Erro ao alternar status");
    }
  };

  const removeForm = async (form) => {
    if (
      !window.confirm(
        `Excluir "${form.title}"? As respostas já recebidas serão mantidas para auditoria.`,
      )
    )
      return;
    try {
      await axios.delete(`${API}/admin/forms/${form.id}`, {
        headers: headers(token),
      });
      toast.success("Excluído");
      load();
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  return (
    <div className="space-y-3" data-testid="admin-forms-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-base font-semibold flex items-center gap-2 text-slate-900">
            <ClipboardList className="w-4 h-4 text-indigo-600" />
            Formulários AVI
          </p>
          <p className="text-xs text-slate-500">
            Crie formulários públicos (sem login) para receber cadastros e
            documentos de candidatos. Cada envio é gravado aqui e enviado
            por e-mail para <strong>leilianeoliveira@grupoavi.com.br</strong>.
          </p>
        </div>
        <Button onClick={startCreate} data-testid="forms-new-btn" className="bg-indigo-600 hover:bg-indigo-700 text-white">
          <Plus className="w-4 h-4 mr-1" />
          Novo formulário
        </Button>
      </div>

      {loading ? (
        <p className="text-xs text-slate-400">
          <Loader2 className="inline w-3.5 h-3.5 mr-1 animate-spin" />
          Carregando…
        </p>
      ) : forms.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-sm text-slate-500">
            Nenhum formulário criado. Clique em "Novo formulário" para começar.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {forms.map((f) => (
            <motion.div
              key={f.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className={f.active ? "" : "opacity-60"}>
                <CardContent className="p-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900 text-sm truncate">
                        {f.title}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {f.description || "—"}
                      </p>
                    </div>
                    <Badge variant={f.active ? "default" : "outline"} className={f.active ? "bg-emerald-600" : ""}>
                      {f.active ? "Ativo" : "Inativo"}
                    </Badge>
                  </div>

                  <div className="flex items-center gap-3 text-[11px] text-slate-500">
                    <span>{(f.fields || []).length} campo(s)</span>
                    <span>·</span>
                    <span>
                      <Inbox className="inline w-3 h-3 mr-0.5" />
                      {f.submissions_count || 0} resposta(s)
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-1.5 pt-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyPublicLink(f.id)}
                      data-testid={`forms-copy-${f.id}`}
                    >
                      <Copy className="w-3.5 h-3.5 mr-1" />
                      Copiar link
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.open(`/form/${f.id}`, "_blank")}
                    >
                      <ExternalLink className="w-3.5 h-3.5 mr-1" />
                      Abrir
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSubmissionsFor(f)}
                      data-testid={`forms-submissions-${f.id}`}
                    >
                      <Eye className="w-3.5 h-3.5 mr-1" />
                      Respostas
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => startEdit(f)}
                    >
                      <Edit2 className="w-3.5 h-3.5 mr-1" />
                      Editar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => toggleActive(f)}
                    >
                      {f.active ? (
                        <ToggleLeft className="w-3.5 h-3.5 mr-1" />
                      ) : (
                        <ToggleRight className="w-3.5 h-3.5 mr-1" />
                      )}
                      {f.active ? "Desativar" : "Ativar"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => removeForm(f)}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {editing && (
        <FormEditor
          token={token}
          form={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
      {submissionsFor && (
        <SubmissionsDialog
          token={token}
          form={submissionsFor}
          onClose={() => setSubmissionsFor(null)}
        />
      )}
    </div>
  );
};

// =============================================================================
// FormEditor — create / edit dialog
// =============================================================================
const FormEditor = ({ token, form, onClose, onSaved }) => {
  const [draft, setDraft] = useState(form);
  const [saving, setSaving] = useState(false);

  const update = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const addField = (type = "text") =>
    update({
      fields: [
        ...(draft.fields || []),
        {
          id: newId(),
          label: "",
          type,
          required: true,
          options: ["select", "radio", "checkbox"].includes(type)
            ? ["Opção 1", "Opção 2"]
            : [],
        },
      ],
    });

  const removeField = (idx) =>
    update({ fields: draft.fields.filter((_, i) => i !== idx) });

  const moveField = (idx, delta) => {
    const next = [...draft.fields];
    const target = idx + delta;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    update({ fields: next });
  };

  const updateField = (idx, patch) => {
    const next = [...draft.fields];
    next[idx] = { ...next[idx], ...patch };
    update({ fields: next });
  };

  const save = async () => {
    if (!(draft.title || "").trim()) {
      toast.error("Informe o título do formulário");
      return;
    }
    if (!(draft.fields || []).length) {
      toast.error("Adicione ao menos um campo");
      return;
    }
    if (draft.fields.some((f) => !(f.label || "").trim())) {
      toast.error("Todos os campos precisam de um nome");
      return;
    }
    setSaving(true);
    try {
      if (draft.id) {
        await axios.put(
          `${API}/admin/forms/${draft.id}`,
          {
            title: draft.title,
            description: draft.description,
            active: draft.active,
            notification_email: draft.notification_email || "",
            fields: draft.fields,
          },
          { headers: headers(token) },
        );
        toast.success("Formulário atualizado");
      } else {
        await axios.post(
          `${API}/admin/forms`,
          {
            title: draft.title,
            description: draft.description,
            active: draft.active,
            notification_email: draft.notification_email || "",
            fields: draft.fields,
          },
          { headers: headers(token) },
        );
        toast.success("Formulário criado");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const needsOptions = (t) => ["select", "radio", "checkbox"].includes(t);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="form-editor-dialog">
        <DialogHeader>
          <DialogTitle>
            {draft.id ? "Editar formulário" : "Novo formulário"}
          </DialogTitle>
          <DialogDescription>
            Defina título e os campos. Depois copie o link público e
            compartilhe com os candidatos.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label>Título *</Label>
            <Input
              value={draft.title}
              onChange={(e) => update({ title: e.target.value })}
              placeholder="Ex.: CADASTRO AVI SEGURANÇA - BAHIA"
              data-testid="form-title-input"
            />
          </div>
          <div>
            <Label>Descrição</Label>
            <Textarea
              value={draft.description || ""}
              onChange={(e) => update({ description: e.target.value })}
              placeholder="Aparece abaixo do título no formulário público"
              rows={2}
            />
          </div>

          <div>
            <Label htmlFor="form-notification-email">
              E-mail de notificação (opcional)
            </Label>
            <Input
              id="form-notification-email"
              type="email"
              value={draft.notification_email || ""}
              onChange={(e) => update({ notification_email: e.target.value })}
              placeholder="Ex.: jaimebarbosa@grupoavi.com.br"
              data-testid="form-notification-email-input"
            />
            <p className="text-xs text-slate-500 mt-1">
              Quando preenchido, as respostas deste formulário irão para este
              e-mail em vez do RH padrão.
            </p>
          </div>

          <div className="border-t pt-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold">Campos</p>
              <div className="flex gap-1">
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      addField(e.target.value);
                      e.target.value = "";
                    }
                  }}
                  className="text-xs border border-slate-300 rounded px-2 py-1 bg-white"
                  defaultValue=""
                  data-testid="form-add-field-select"
                >
                  <option value="" disabled>
                    + Adicionar campo
                  </option>
                  {FIELD_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              {(draft.fields || []).map((f, idx) => (
                <div
                  key={f.id || idx}
                  className="border border-slate-200 rounded p-2 bg-slate-50/50"
                >
                  <div className="flex items-center gap-2">
                    <GripVertical className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <Input
                      value={f.label}
                      onChange={(e) =>
                        updateField(idx, { label: e.target.value })
                      }
                      placeholder="Rótulo do campo"
                      className="flex-1 text-sm"
                      data-testid={`form-field-label-${idx}`}
                    />
                    <select
                      value={f.type}
                      onChange={(e) =>
                        updateField(idx, {
                          type: e.target.value,
                          options: needsOptions(e.target.value)
                            ? f.options?.length
                              ? f.options
                              : ["Opção 1", "Opção 2"]
                            : [],
                        })
                      }
                      className="text-xs border border-slate-300 rounded px-2 py-1.5 bg-white"
                    >
                      {FIELD_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                    <label className="flex items-center gap-1 text-xs whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={!!f.required}
                        onChange={(e) =>
                          updateField(idx, { required: e.target.checked })
                        }
                      />
                      Obrig.
                    </label>
                    <button
                      onClick={() => moveField(idx, -1)}
                      className="text-slate-500 hover:bg-slate-200 rounded p-1"
                      aria-label="Subir"
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => moveField(idx, +1)}
                      className="text-slate-500 hover:bg-slate-200 rounded p-1"
                      aria-label="Descer"
                    >
                      ↓
                    </button>
                    <button
                      onClick={() => removeField(idx)}
                      className="text-red-500 hover:bg-red-50 rounded p-1"
                      aria-label="Remover"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  {needsOptions(f.type) && (
                    <div className="ml-6 mt-1.5">
                      <Label className="text-[10px] text-slate-500">
                        Opções (uma por linha)
                      </Label>
                      <Textarea
                        value={(f.options || []).join("\n")}
                        onChange={(e) =>
                          updateField(idx, {
                            options: e.target.value
                              .split("\n")
                              .map((s) => s.trim())
                              .filter(Boolean),
                          })
                        }
                        rows={Math.min(8, Math.max(2, (f.options || []).length))}
                        className="text-xs"
                      />
                    </div>
                  )}
                </div>
              ))}
              {(draft.fields || []).length === 0 && (
                <p className="text-xs text-slate-400 text-center py-3">
                  Use o seletor acima para adicionar o primeiro campo.
                </p>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={save} disabled={saving} data-testid="form-save-btn" className="bg-indigo-600 hover:bg-indigo-700 text-white">
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Salvando…
              </>
            ) : (
              "Salvar"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =============================================================================
// SubmissionsDialog — read-only list of received submissions
// =============================================================================
const SubmissionsDialog = ({ token, form, onClose }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opened, setOpened] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(
          `${API}/admin/forms/${form.id}/submissions`,
          { headers: headers(token) },
        );
        setItems(r.data?.items || []);
      } catch {
        toast.error("Erro ao carregar respostas");
      } finally {
        setLoading(false);
      }
    })();
  }, [token, form.id]);

  const fieldLabelMap = Object.fromEntries(
    (form.fields || []).map((f) => [f.id, f.label]),
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Respostas — {form.title}</DialogTitle>
          <DialogDescription>
            {items.length} resposta(s) recebida(s) até agora.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="text-xs text-slate-400">Carregando…</p>
        ) : items.length === 0 ? (
          <p className="text-center text-sm text-slate-500 py-6">
            Ainda não há respostas para este formulário.
          </p>
        ) : (
          <div className="space-y-2">
            {items.map((s) => (
              <Card key={s.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setOpened(s)}>
                <CardContent className="p-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-xs font-mono text-slate-600">
                        #{s.id.slice(0, 8)}
                      </p>
                      <p className="text-[11px] text-slate-500">
                        {new Date(s.submitted_at).toLocaleString("pt-BR")} ·
                        IP {s.client_ip || "—"} ·
                        {(s.files || []).length} anexo(s)
                      </p>
                    </div>
                    <Button size="sm" variant="outline">
                      Ver detalhes
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {opened && (
          <Dialog open onOpenChange={(o) => !o && setOpened(null)}>
            <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Resposta #{opened.id.slice(0, 8)}</DialogTitle>
                <DialogDescription>
                  {new Date(opened.submitted_at).toLocaleString("pt-BR")} ·
                  IP {opened.client_ip || "—"}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2 text-sm">
                {Object.entries(opened.answers || {}).map(([fid, val]) => (
                  <div key={fid}>
                    <p className="text-[10px] uppercase tracking-wide text-slate-400">
                      {fieldLabelMap[fid] || fid}
                    </p>
                    <p className="text-slate-800">
                      {Array.isArray(val) ? val.join(", ") : (val || "—")}
                    </p>
                  </div>
                ))}
                {(opened.files || []).length > 0 && (
                  <div className="border-t pt-2">
                    <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">
                      Anexos
                    </p>
                    {opened.files.map((f, i) => (
                      <a
                        key={i}
                        href={`${API}/admin/form-submissions/${opened.id}/file/${i}?admin_token=${encodeURIComponent(token)}`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-2 text-xs text-indigo-700 hover:underline py-0.5"
                      >
                        <Download className="w-3 h-3" />
                        {fieldLabelMap[f.field_id] || f.field_id}: {f.filename}{" "}
                        <span className="text-slate-400">({f.size} bytes)</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AdminFormsTab;
